// Companions (Mitras) API client.
//
// Same auth model as lib/chats.js: Bearer for signed-in users, X-Anon-Uid
// for anonymous browsers. On sign-in, claimAnonCompanions() transfers
// the anon's mitras to their real account.

import { getToken } from './auth'
import { getAnonUserId } from './localChats'

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '')

async function ownerFetch(path, opts = {}) {
  const token = getToken()
  const uid = token ? null : getAnonUserId()
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : (uid ? { 'X-Anon-Uid': uid } : {})),
      ...(opts.headers || {}),
    },
  })
  let data = null
  try { data = await res.json() } catch {}
  if (!res.ok) {
    const err = new Error(data?.error?.message || `HTTP ${res.status}`)
    err.status = res.status
    err.code = data?.error?.code
    if (data?.error?.kind) err.kind = data.error.kind
    if (data?.usage) err.usage = data.usage
    throw err
  }
  return data
}

// Anonymous endpoint — no owner header needed, server returns the static
// catalog of starter personas.
export async function listPresets() {
  const res = await fetch(`${API_BASE}/api/companions/presets`)
  if (!res.ok) return []
  try {
    const data = await res.json()
    return data?.presets || []
  } catch { return [] }
}

export async function listCompanions() {
  const data = await ownerFetch('/api/companions')
  return data?.companions || []
}

export async function getCompanion(id) {
  const data = await ownerFetch(`/api/companions/${encodeURIComponent(id)}`)
  return data?.companion
}

// Lazily create a per-owner instance of a preset persona. Idempotent —
// the server returns the existing row if it already exists.
export async function instantiatePreset(archetype) {
  const data = await ownerFetch(`/api/companions/preset/${encodeURIComponent(archetype)}`, {
    method: 'POST',
  })
  return data?.companion
}

export async function createCustomCompanion({ name, description, traits, appearance, avatarUrl, assistantId, systemPrompt }) {
  const data = await ownerFetch('/api/companions', {
    method: 'POST',
    body: JSON.stringify({ name, description, traits, appearance, avatarUrl, assistantId, systemPrompt }),
  })
  return data?.companion
}

// Open an AI Assistant in the Companion chat view. Idempotent — looks
// up an existing companion that was materialised from this assistant
// and returns it; if none exists, creates one with the assistant's
// portrait + hand-tuned system prompt and returns the new row.
export async function instantiateAssistant(assistant) {
  if (!assistant || !assistant.id) {
    throw new Error('instantiateAssistant: assistant object required')
  }
  const mine = await listCompanions()
  const existing = mine.find((c) => c.assistantId === assistant.id)
  if (existing) return existing
  return await createCustomCompanion({
    name: assistant.name,
    description: assistant.tagline || assistant.desc?.slice(0, 280) || '',
    traits: assistant.caps || [],
    // Appearance prompt isn't used (no avatar gen) but stored for reference.
    appearance: assistant.tagline || '',
    avatarUrl: `${process.env.PUBLIC_URL || ''}/assistants/${assistant.id}.jpg`,
    assistantId: assistant.id,
    systemPrompt: assistant.systemPrompt,
  })
}

export async function deleteCompanion(id) {
  await ownerFetch(`/api/companions/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// Edit a custom character. Server only patches the fields you send and
// requires the character to be private/rejected (Unpublish first if not).
// regeneratePrompt=true re-synthesises the persona system prompt via the
// LLM meta-prompt — costs ~500 tokens, off by default.
export async function updateCompanion(id, fields = {}) {
  const { name, description, traits, appearance, avatarUrl, regeneratePrompt } = fields
  const data = await ownerFetch(`/api/companions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ name, description, traits, appearance, avatarUrl, regeneratePrompt }),
  })
  return data?.companion
}

// Marketplace flow: creator submits their private persona for admin
// review. Server requires a signed-in user — anon can't submit.
// Returns the updated summary with visibility='pending'.
export async function submitForReview(id) {
  const data = await ownerFetch(`/api/companions/${encodeURIComponent(id)}/submit-for-review`, {
    method: 'POST',
  })
  return data?.companion
}

// Pull a persona back to private (from pending or public).
export async function unpublishCompanion(id) {
  const data = await ownerFetch(`/api/companions/${encodeURIComponent(id)}/unpublish`, {
    method: 'POST',
  })
  return data?.companion
}

// ── Discovery: public catalog + voting + clone-on-use ────────────

// Sorted by rankScore desc. Server excludes the viewer's own
// creations so they don't appear under both sections.
export async function listPopular() {
  const data = await ownerFetch('/api/companions/popular')
  return data?.companions || []
}

export async function getMyVote(ownerEmail, companionId) {
  const data = await ownerFetch(
    `/api/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}/my-vote`,
  )
  return data?.vote || null
}

// vote = 'like' | 'dislike' | null. Returns {likes, dislikes, myVote}.
// Throws if the viewer isn't signed in (server enforces).
export async function voteCompanion(ownerEmail, companionId, vote) {
  const data = await ownerFetch(
    `/api/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}/vote`,
    { method: 'POST', body: JSON.stringify({ vote }) },
  )
  return data   // { likes, dislikes, myVote }
}

// Clone a public persona into the viewer's roster. Idempotent — re-
// calling returns the existing clone instead of duplicating.
// (Not named `useCompanion` because the `use*` prefix triggers React's
// rules-of-hooks lint everywhere this is imported.)
export async function cloneFromPublic(ownerEmail, companionId) {
  const data = await ownerFetch(
    `/api/companions/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(companionId)}/use`,
    { method: 'POST' },
  )
  return data?.companion
}

export async function generateAvatar(id, { appearance } = {}) {
  const data = await ownerFetch(`/api/companions/${encodeURIComponent(id)}/avatar`, {
    method: 'POST',
    body: JSON.stringify({ appearance: appearance || '' }),
  })
  return data   // { companion, usage }
}

// Preview an avatar before the companion row exists. Generates the
// image on the server (counts against image quota) and returns just
// the URL — no companion is created. The CompanionCreator uses this
// to show a preview the user can accept or regenerate.
export async function previewAvatar({ appearance } = {}) {
  const data = await ownerFetch('/api/companions/preview-avatar', {
    method: 'POST',
    body: JSON.stringify({ appearance: appearance || '' }),
  })
  return data   // { avatarUrl, usage }
}

// Send a chat turn. Server stores history server-side, so we only send
// the new question. Returns { answer, usage, companion }.
export async function companionChat(id, { question, signal } = {}) {
  const token = getToken()
  const uid = token ? null : getAnonUserId()
  const res = await fetch(`${API_BASE}/api/companions/${encodeURIComponent(id)}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : (uid ? { 'X-Anon-Uid': uid } : {})),
    },
    body: JSON.stringify({ question }),
    signal,
  })
  let data = null
  try { data = await res.json() } catch {}
  if (!res.ok) {
    const err = new Error(data?.error?.message || `HTTP ${res.status}`)
    err.status = res.status
    err.code = data?.error?.code
    if (data?.error?.kind) err.kind = data.error.kind
    if (data?.usage) err.usage = data.usage
    throw err
  }
  return data
}

export async function listMemories(id) {
  const data = await ownerFetch(`/api/companions/${encodeURIComponent(id)}/memories`)
  return data?.memories || []
}

export async function deleteMemory(id, memoryId) {
  const data = await ownerFetch(
    `/api/companions/${encodeURIComponent(id)}/memories/${encodeURIComponent(memoryId)}`,
    { method: 'DELETE' }
  )
  return data?.memories || []
}

export async function clearAllMemories(id) {
  await ownerFetch(`/api/companions/${encodeURIComponent(id)}/memories`, { method: 'DELETE' })
}

export async function claimAnonCompanions() {
  const token = getToken()
  if (!token) return { claimed: 0 }
  let uid = null
  try { uid = getAnonUserId() } catch {}
  if (!uid) return { claimed: 0 }
  const res = await fetch(`${API_BASE}/api/companions/claim`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Anon-Uid': uid,
    },
  })
  let data = null
  try { data = await res.json() } catch {}
  if (!res.ok) {
    const err = new Error(data?.error?.message || `HTTP ${res.status}`)
    err.status = res.status
    throw err
  }
  return data || { claimed: 0 }
}
